﻿import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { forkJoin, from, of } from 'rxjs';
import { Utils } from '@shared/fork-common-controls/utils.service';

// For more info/documentation on the library used here,
// go to https://mozilla.github.io/pdf.js.
const pdfjsLib = require('pdfjs-dist');
pdfjsLib.PDFJS.disableWorker = true;

@Component({
  selector: 'ula-pdf',
  template: `
    <iframe
      #frame
      class="pdf-container"
      frameBorder="0"
      [style.width]="'100%'"
      [style.height]="canvasDisplayContainerHeight">
    </iframe>

    <iframe
      #framePrint
      class="pdf-container"
      frameBorder="0"
      [style.width]="'0px'"
      [style.height]="'0px'">
    </iframe>
  `,
  styles: [
    ':host { display: block; text-align: center; }',
    '.pdf-container { display: inline-block }'
  ]
})
export class UlaPdfComponent implements AfterViewInit, OnChanges {

  // This is an attempt to push down the height on IE display canvas enough
  // to where it doesn't produce a scroll bar that goes nowhere.
  private static PDF_DISPLAY_HEIGHT_BUFFER = 100;

  private iframe: HTMLIFrameElement;
  private iframePrint: HTMLIFrameElement;
  private canvasDisplayContainer: HTMLElement;
  private canvasPrintContainer: HTMLElement;
  @Input() public pdfData: string;
  @Input() public showPointer = false;
  @Output() private onPdfPageHeightCalculated: EventEmitter<number> = new EventEmitter<number>();
  @Output() private onPdfRenderComplete: EventEmitter<void> = new EventEmitter<void>();
  @Output() private onClick: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('frame') frame: ElementRef;
  @ViewChild('framePrint') framePrint: ElementRef;
  public canvasDisplayContainerHeight = '100%';
  private firstPageTopMargin = 50;
  private verticalSpaceBetweenEachPage = 100;

  private initialized = false;
  private isLoading = true;

  /**
   * Set up an iframe to be used only for displaying PDFs.
   */
  private setUpDisplayIFrame(): void {
    this.iframe = this.frame.nativeElement;
    const pdfFrame = 'pdf-frame'; // Used as id for iframe content body.

    const iframeElementContainer = this.iframe.contentWindow.document;
    iframeElementContainer.open();
    iframeElementContainer.writeln(`<html><body id="${pdfFrame}"></body></html>`);
    iframeElementContainer.close();

    const body = iframeElementContainer.getElementById(pdfFrame);
    body.style.margin = '0';
    body.onclick = () => this.onClick.emit();

    if (this.showPointer) {
      body.style.cursor = 'pointer';
    }

    const style = document.createElement('style');
    style.media = 'print';
    style.type = 'text/css';
    style.innerText = `@page {
        margin: 0mm;  /* this affects the margin in the printer settings */
      }`;

    body.appendChild(style);
    this.canvasDisplayContainer = iframeElementContainer.createElement('div');
    body.appendChild(this.canvasDisplayContainer);
  }

  /**
   * Set up an iframe to be used only for printing PDFs.
   */
  setupPrintIFrame(): void {
    this.iframePrint = this.framePrint.nativeElement;
    const pdfFrame = 'pdf-frame-print'; // Used as id for iframe content body.

    const iframeElementContainer = this.iframePrint.contentWindow.document;
    iframeElementContainer.open();
    iframeElementContainer.writeln(`<html><body id="${pdfFrame}"></body></html>`);
    iframeElementContainer.close();

    const body = iframeElementContainer.getElementById(pdfFrame);
    body.style.margin = '0';
    body.onclick = () => this.onClick.emit();

    if (this.showPointer) {
      body.style.cursor = 'pointer';
    }

    const style = document.createElement('style');
    style.media = 'print';
    style.type = 'text/css';
    style.innerText = `@page {
        margin: 0mm;  /* this affects the margin in the printer settings */
      }`;

    body.appendChild(style);
    this.canvasPrintContainer = iframeElementContainer.createElement('div');
    body.appendChild(this.canvasPrintContainer);
  }

  ngOnChanges({ pdfData }: SimpleChanges): void {
    if (pdfData && this.initialized) {
      this.renderPdf();
    }
  }

  /**
   * Set up two iframes: one for displaying and one for printing PDFs.
   */
  ngAfterViewInit() {
    this.initialized = true;
    this.isLoading = true;
    this.setUpDisplayIFrame();
    this.setupPrintIFrame();
    this.renderPdf();
  }

  /**
   * Render the PDF document represented by a 64 bit encoded string stored in this.pdfData.
   */
  private renderPdf(): void {

    // Clear previous PDFs from display container.
    const container = this.canvasDisplayContainer;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Clear previous PDFs from print container.
    const containerForPrinting = this.canvasPrintContainer;
    while (containerForPrinting.firstChild) {
      containerForPrinting.removeChild(containerForPrinting.firstChild);
    }

    this.getPdfDocument(this.pdfData).then(this.renderPages);
  }

  /**
   * Print the contents of the iframe, which in turn contains a collection of PDFs rendered on canvases.
   */
  public print(): void {
    const { contentWindow } = this.iframePrint;
    const result = contentWindow.document.execCommand('print', false, null);

    // If the above doesn't work (which is mainly for IE) use this.
    if (!result) {
      contentWindow.print();
    }
  }

  /**
   *
   * Get a PDF document from a 64 bit encoded string.
   *
   * @param {string} encoded64BitStr - A 64 bit encoded string of a PDF.
   * @returns {Promise<any>} - A PDFDocumentProxy object native to the pdfjs framework that
   * encapsulates the 64 bit string representation of a PDF.
   */
  private getPdfDocument(encoded64BitStr: string): Promise<any> {
    const decoded64BitStr = atob(encoded64BitStr);
    return pdfjsLib.getDocument({data: decoded64BitStr}).promise;
  }

  private onAppendCanvasError = error => {
    console.error(error);
  }

  // Element means different things depending on browser in use:
  // IE - element is a canvas
  // Not IE - element is a div containing a canvas
  private appendCanvasesToContainer = (canvasResults: any[]) => {
    canvasResults.forEach(canvas => this.canvasDisplayContainer.appendChild(canvas));
    let sumOfCanvasHeights = canvasResults
      .map(element => {
        let height: number;

        if (!Utils.isBrowserIE()) {
          height = element.children[0].clientHeight;
        } else {
          height = element.clientHeight;
        }

        // Add a spacing between each rendered PDF page.
        return height + this.verticalSpaceBetweenEachPage;
      })
      .reduce((total, current) => total + current);

    // Omit the trailing space on the last rendered PDF page.
    sumOfCanvasHeights -= this.verticalSpaceBetweenEachPage;

    // Enforce a buffer to prevent unnecessary scroll bars in IE.
    const buffer = UlaPdfComponent.PDF_DISPLAY_HEIGHT_BUFFER + this.firstPageTopMargin;
    const totalHeight = sumOfCanvasHeights + buffer;
    this.canvasDisplayContainerHeight = totalHeight + 'px';

    this.onPdfPageHeightCalculated.emit(totalHeight);
    this.onPdfRenderComplete.emit();
  }

  // Element means different things depending on browser in use:
  // IE - element is a canvas
  // Not IE - element is a div containing a canvas
  private appendCanvasesToPrintContainer = (canvasResults: any[]) => {
    canvasResults.forEach(canvas => this.canvasPrintContainer.appendChild(canvas));
    this.isLoading = false;
  }

  /**
   *
   * Given a PDF document produced by pdfjs, loop through each page and render it to a canvas.
   *
   * @param pdfDocument - a PDF document produced by pdfjs.
   */
  private renderPages = (pdfDocument: any): void => {
    const canvasesForDisplay: Observable<any>[] = [];
    const canvasesForPrint: Observable<any>[] = [];
    const totalNumPages: number = pdfDocument.numPages;

    for (let currentPageNum = 1; currentPageNum <= totalNumPages; currentPageNum++) {
      const page = from(pdfDocument.getPage(currentPageNum));
      const toDisplay = (pdfPage): Observable<any> => this.renderPageToDisplayCanvas(
        pdfPage,
        currentPageNum === 1,
        currentPageNum,
        totalNumPages);
      const toPrint = (pdfPage): Observable<any> => this.renderPageToPrintCanvas(
        pdfPage,
        currentPageNum === 1,
        currentPageNum,
        totalNumPages);
      const canvasForDisplay: Observable<any> = page.flatMap(pdfPage => toDisplay(pdfPage));
      const canvasForPrint: Observable<any> = page.flatMap(pdfPage => toPrint(pdfPage));
      canvasesForDisplay.push(canvasForDisplay);
      canvasesForPrint.push(canvasForPrint);
    }

    forkJoin(canvasesForDisplay).subscribe(this.appendCanvasesToContainer, this.onAppendCanvasError);
    forkJoin(canvasesForPrint).subscribe(this.appendCanvasesToPrintContainer, this.onAppendCanvasError);
  }

  /**
   *
   * Render a single page for display use from a PDF document produced by pdfjs to a canvas.
   *
   * @param pdfPage - a single page from a PDF document produced by pdfjs.
   * @param {boolean} setDimensions - whether or not we want to set dimensions of the parent canvas container (note
   * that we only want to do this once per collection of pages, i.e. only the first page in the collection).
   * @param {number} currentPageNum - the current page number this PDF page represents in the PDF document.
   * @param {number} numOfPages - the number of pages in the PDF document.
   * @returns {Observable<any>} - the resulting div/canvas HTML element that has the corresponding PDF page rendered to it.
   */
  private renderPageToDisplayCanvas = (pdfPage, setDimensions: boolean, currentPageNum: number, numOfPages: number): Observable<any> => {

    const isBrowserIE = Utils.isBrowserIE();

    const scale = 5;
    const viewport = pdfPage.getViewport(scale);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = '100%';

    // Make sure that we put a top margin on the first page so that it's not right up against the print icon.
    canvas.style.marginTop = (currentPageNum === 1 ? this.firstPageTopMargin : 0) + 'px';

    // Make sure we don't put a vertical space on the last page.
    canvas.style.marginBottom = (currentPageNum === numOfPages ? 0 : this.verticalSpaceBetweenEachPage) + 'px';

    const renderContext = {
      canvasContext: ctx,
      viewport
    };

    const renderPromise = pdfPage.render(renderContext);
    const observePageRender = from(renderPromise);

    // Depending on whether we're displaying/printing in IE, style differently.
    let canvasOrWrapper;
    if (!isBrowserIE) {
      canvas.style.minWidth = Math.floor(viewport.width / scale) + 'px';

      const wrapper = document.createElement('div');
      wrapper.style.width = '100%';
      wrapper.appendChild(canvas);

      canvasOrWrapper = wrapper;
    } else {
      canvasOrWrapper = canvas;
    }
    return observePageRender.flatMap(() => of(canvasOrWrapper));
  }

  /**
   *
   * Render a single page for printing use from a PDF document produced by pdfjs to a canvas.
   *
   * @param pdfPage - a single page from a PDF document produced by pdfjs.
   * @param {boolean} setDimensions - whether or not we want to set dimensions of the parent canvas container (note
   * that we only want to do this once per collection of pages, i.e. only the first page in the collection).
   * @param {number} currentPageNum - the current page number this PDF page represents in the PDF document.
   * @param {number} numOfPages - the number of pages in the PDF document.
   * @returns {Observable<any>} - the resulting div/canvas HTML element that has the corresponding PDF page rendered to it.
   */
  private renderPageToPrintCanvas = (pdfPage: any, setDimensions: boolean, currentPageNum: number, numOfPages: number): Observable<any> => {

    const isBrowserIE = Utils.isBrowserIE();

    const scale = 5;
    const viewport = pdfPage.getViewport(scale);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.height = viewport.height;
    canvas.width = viewport.width;
    canvas.style.height = '100%';
    canvas.style.width = '100%';

    const renderContext = {
      canvasContext: ctx,
      viewport
    };

    const renderPromise = pdfPage.render(renderContext);
    const observePageRender = from(renderPromise);

    // Depending on whether we're displaying/printing in IE, style differently.
    let canvasOrWrapper;
    if (!isBrowserIE) {
      const wrapper = document.createElement('div');
      wrapper.style.width = '100%';
      wrapper.style.height = '100%';
      wrapper.appendChild(canvas);

      canvasOrWrapper = wrapper;
    } else {
      canvasOrWrapper = canvas;
    }
    return observePageRender.flatMap(() => of(canvasOrWrapper));
  }
}
